// EmergencyLockoutGate — the Bailout Portal (Phase 9b, 2026-05-11).
//
// Fires when /admin/auth/me returns marginStatus === 'locked' (wallet
// + deposit ≤ 0). Hard overlay (z-9999, body overflow lock, ESC
// suppression, no close button) — the agent cannot navigate the
// rest of the app until they bail themselves out.
//
// Phase 9b v2 redesign (2026-05-11 PM): agents reported the original
// portal looked like a generic error, not a guided recovery flow.
// This version leads with PLAIN-LANGUAGE storytelling:
//
//   1. Header — big rose gradient, "Account locked" + one-line cause.
//   2. WHY THIS HAPPENED — plain English: your wallet went negative
//      because of game settlements; your deposit was the safety net;
//      it's now consumed.
//   3. HOW CLOSE TO UNLOCK — large progress bar with the shortfall
//      number. Negative sum → positive sum is the journey.
//   4. TWO WAYS OUT — two big tappable tiles directing them to the
//      inbox action (Section A) or the upline payment form (Section
//      C). These are the primary CTAs.
//   5. YOUR POWERS — what they CAN still do (green ticks) and what's
//      temporarily BLOCKED (amber crosses). Removes the "is the
//      website broken?" confusion.
//   6. SECTIONS A+B+C — the actual inbox queues + emergency form.
//
// Every approval action triggers an auth refresh so the gate
// auto-unmounts the moment wallet+deposit climbs back > 0.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAdminAuth } from '@baaligames/shared-ui/admin/lib/adminAuth';
import { useToast } from '@baaligames/shared-ui/lib/toast';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { refreshAgentWallet } from '@baaligames/shared-ui/admin/lib/agentWallet';
import { ApiError } from '@baaligames/shared-ui/lib/api';
import { useT } from '@baaligames/shared-ui/lib/i18n';
import Icon from '@baaligames/shared-ui/components/Icon';
import EmergencyRechargeForm from './EmergencyRechargeForm';

const inr = (n: number) => `₹${Math.round(n || 0).toLocaleString('en-IN')}`;
const inrSigned = (n: number) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${inr(Math.abs(n || 0))}`;

export default function EmergencyLockoutGate() {
  const { admin, refresh } = useAdminAuth();
  const { push } = useToast();
  const t = useT();
  const [refreshing, setRefreshing] = useState(false);
  const [inboxOpen, setInboxOpen]   = useState(false);
  const [uplineOpen, setUplineOpen] = useState(false);

  // ESC suppression — non-dismissible.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, []);

  // Body overflow lock.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const refetchStatus = useCallback(async () => {
    setRefreshing(true);
    try {
      await refresh();
      refreshAgentWallet();
    } catch { /* silent — explicit refresh button surfaces its own toast */ }
    finally { setRefreshing(false); }
  }, [refresh]);

  // Live balances from /auth/me payload.
  const wallet  = Number((admin as any)?.walletBalance)  | 0;
  const deposit = Number((admin as any)?.depositBalance) | 0;
  const negWallet = wallet < 0 ? -wallet : 0;

  // Phase 9b v2: the recovery targets the form will charge against.
  //   bareMinimum = |wallet+deposit|            — risky, re-locks instantly
  //   safeMinimum = deposit + 0.5 × |wallet|    — recommended (real cushion)
  //   recommended = deposit + 1.0 × |wallet|    — best (full recovery)
  const bareMinimum = Math.max(0, -(wallet + deposit));
  const safeMinimum = deposit + Math.ceil(0.5 * negWallet);
  const recommended = deposit + negWallet;

  // Progress gauge — measures how close the agent is to the SAFE
  // recovery target. The gauge starts at 0% when wallet is at the
  // deepest breach (wallet = −∞ relative to safeMinimum target) and
  // reaches 100% when the agent's NEXT payment of safeMinimum would
  // lift them comfortably out. Practical computation:
  //   gap = safeMinimum                (how much they need to add)
  //   already_paid = max(0, safeMinimum − gap_remaining)
  // We approximate already_paid as the cushion they still have over
  // the "deepest" point (where wallet = -∞). Simpler: just show
  // "₹X out of ₹safeMinimum still needed" framed as a percentage.
  // Since they always need to add `safeMinimum` to wallet to reach
  // safety, the % "recovered" reflects how much of the cushion they
  // currently have above zero-sum:
  //   pct = clamp( (wallet + safeMinimum − negWallet) / safeMinimum × 100 )
  // Simplified: when wallet=-negWallet (initial locked state), pct=0%.
  // When wallet=0 (after paying negWallet, still risky), pct=50%.
  // When wallet=+deposit (after paying safeMinimum effectively),
  // pct=100%. Wraps the meter to the SAFE target, not the bare line.
  const progressPct = useMemo(() => {
    if (safeMinimum <= 0) return 100;
    // Current "cushion" relative to where we started (deepest negWallet).
    // Initial state (just-locked): wallet = -negWallet, deposit = deposit.
    // After paying X: wallet = -negWallet + X.
    // We want pct = X / safeMinimum * 100, clamped.
    // X currently = (wallet + negWallet) = wallet + |wallet_initial|.
    // BUT wallet is the CURRENT wallet — we don't know the original.
    // Pragmatic approach: take the cushion above bareMinimum line.
    const paidSoFar = Math.max(0, wallet + negWallet); // ≥0 means above original
    void paidSoFar;
    // Simplest honest gauge: how close current SUM is to the safe sum
    // (deposit + half-cushion = safeMinimum total).
    const currentSum = wallet + deposit;
    const targetSum  = Math.ceil(0.5 * negWallet);     // wallet after paying safeMin = +0.5*negWallet
    // currentSum runs from -negWallet (worst) → targetSum + deposit (safe target)
    // Map that range to [0, 100].
    const worst  = -negWallet;
    const target = targetSum + deposit;
    if (target <= worst) return 100;
    const pct = ((currentSum - worst) / (target - worst)) * 100;
    return Math.max(0, Math.min(100, pct));
  }, [wallet, deposit, negWallet, safeMinimum]);

  return (
    // Phase 9b v3 fix: was `grid place-items-center` which vertically
    // centred the modal. When content exceeded viewport height the
    // top got clipped — the grid container positions the child but
    // the child's height makes it overflow above the viewport, and
    // the inner content can't be scrolled into view because the grid
    // re-centres on every resize. Switched to flex column with
    // top-anchored alignment: the modal flows from the top, the
    // wrapper has `overflow-y-auto`, and ANY screen size scrolls
    // naturally. Mobile gets reduced padding so the modal hits the
    // edges (more screen real estate); desktop keeps comfortable
    // gutters.
    // Phase 9b v3 hot-fix: respect the device's safe-area insets on
    // notched phones. The viewport meta already has
    // `viewport-fit=cover`, so env(safe-area-inset-*) returns real
    // pixel values for the notch (top), home indicator (bottom), and
    // rounded landscape edges (left/right). calc() ADDS the inset to
    // the design padding so the modal always sits below the notch
    // with the gutter intact. On unaffected devices env() returns 0
    // and the padding collapses back to the design defaults.
    <div
      // Phase 10 follow-up (2026-05-12): top padding now reserves
      // space for the persistent top bar (AgentNavBar is z-10000).
      // The bar height is ~3.5rem on mobile, ~4rem on desktop, plus
      // the bar's own ~0.75rem top padding. We add env(safe-area-
      // inset-top) on top so notched phones don't tuck the bar under
      // the system status bar. ~0.75rem extra gives a visible gutter
      // between bar bottom and modal card.
      className="fixed inset-0 z-[9999] flex flex-col items-center
                 bg-black/85 backdrop-blur-sm overflow-y-auto overscroll-contain
                 pl-[calc(0.5rem+env(safe-area-inset-left))]
                 pr-[calc(0.5rem+env(safe-area-inset-right))]
                 pt-[calc(env(safe-area-inset-top)+5rem)]
                 pb-[calc(2rem+env(safe-area-inset-bottom))]
                 sm:pl-[calc(1rem+env(safe-area-inset-left))]
                 sm:pr-[calc(1rem+env(safe-area-inset-right))]
                 sm:pt-[calc(env(safe-area-inset-top)+5.5rem)]"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="bailout-portal-title"
    >
      {/* shrink-0 is critical — without it the flex parent compresses
          the card to viewport height and the wrapper's overflow-y-auto
          never engages (cardScrollH === clientH). With shrink-0 the
          card keeps its full intrinsic content height, the wrapper
          recognises the overflow, and the user can scroll naturally
          on every screen size. */}
      <div className="w-full max-w-3xl shrink-0 rounded-3xl
                      bg-white dark:bg-arena-900
                      border-2 border-rose-300 dark:border-rose-500/50
                      shadow-[0_20px_60px_rgba(244,63,94,0.45)]
                      overflow-hidden">

        {/* ─── 1. CRISIS HEADER ──────────────────────────────────── */}
        <div className="relative overflow-hidden bg-gradient-to-br from-rose-600 via-rose-500 to-red-700 px-5 sm:px-7 py-6 text-white">
          {/* Soft halo */}
          <div aria-hidden className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-white/15 blur-3xl" />
          <div aria-hidden className="absolute -bottom-20 -left-20 w-48 h-48 rounded-full bg-orange-400/20 blur-3xl" />
          <div className="relative flex items-start gap-3">
            <span className="grid place-items-center w-14 h-14 rounded-2xl
                             bg-white/15 ring-2 ring-white/30 shrink-0
                             shadow-[0_4px_18px_rgba(0,0,0,0.18)]">
              <Icon name="lock" size={32} filled />
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-[10.5px] font-bold tracking-[0.22em] uppercase opacity-90">
                {t('agent.lockout.header.eyebrow')}
              </div>
              <h1 id="bailout-portal-title"
                  className="text-2xl sm:text-[28px] font-black tracking-tight leading-tight mt-1">
                {t('agent.lockout.header.title')}
              </h1>
              <p className="text-[13px] sm:text-[13.5px] text-rose-100 mt-2 leading-snug max-w-prose">
                {t('agent.lockout.header.body')}
              </p>
            </div>
          </div>
        </div>

        {/* ─── 2. WHY THIS HAPPENED ─────────────────────────────── */}
        <div className="px-5 sm:px-7 py-4 sm:py-5 bg-arena-50/60 dark:bg-arena-700/30">
          <div className="flex items-start gap-3">
            <span className="grid place-items-center w-9 h-9 rounded-xl shrink-0
                             bg-rose-100 text-rose-700
                             dark:bg-rose-500/15 dark:text-rose-300
                             ring-1 ring-rose-200 dark:ring-rose-400/30">
              <Icon name="info" size={18} filled />
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-[10.5px] uppercase tracking-wider font-bold text-arena-700 dark:text-neon-200/85 mb-0.5">
                {t('agent.lockout.why.eyebrow')}
              </div>
              <p className="text-[13px] text-arena-900 dark:text-white leading-snug">
                {t('agent.lockout.why.body')}
              </p>
              <p className="text-[11.5px] text-arena-600 dark:text-neon-200/70 mt-1.5 leading-snug">
                {t('agent.lockout.why.reassurance')}
              </p>
            </div>
          </div>
        </div>

        {/* ─── 3. UNLOCK PROGRESS GAUGE ─────────────────────────── */}
        <div className="px-5 sm:px-7 py-5 border-t border-arena-200/60 dark:border-arena-600/50">
          <div className="flex items-baseline justify-between gap-2 flex-wrap mb-2">
            <div>
              <div className="text-[10.5px] uppercase tracking-wider font-bold text-arena-600 dark:text-neon-200/75">
                {t('agent.lockout.gauge.label')}
              </div>
              <div className="font-display font-black text-3xl sm:text-4xl tabular-nums text-rose-700 dark:text-rose-300 leading-none mt-1">
                {inr(safeMinimum)}
                <span className="ml-2 text-[12px] font-bold uppercase tracking-wider text-arena-600 dark:text-neon-200/75">
                  {t('agent.lockout.gauge.suffix')}
                </span>
              </div>
            </div>
            <span className="text-[11px] font-mono tabular-nums font-extrabold text-rose-700 dark:text-rose-300 px-2 py-0.5 rounded bg-rose-100 dark:bg-rose-500/20">
              {t('agent.lockout.gauge.recoveredBadge', { pct: progressPct.toFixed(0) })}
            </span>
          </div>

          {/* The progress bar — rewritten to read intuitively:
              TRACK = solid rose (the danger zone the agent is in).
              FILL  = emerald (safety) growing from the left.
              At 0% the bar is fully rose. At 100% it's fully emerald.
              This way "more green" always means "more safe". */}
          <div className="relative h-3.5 rounded-full overflow-hidden
                          bg-rose-200 dark:bg-rose-500/35
                          ring-1 ring-rose-300/60 dark:ring-rose-400/40">
            <div
              className="h-full rounded-full transition-all duration-700 ease-out
                         bg-gradient-to-r from-emerald-400 to-emerald-500
                         shadow-[0_0_10px_rgba(16,185,129,0.55)]"
              style={{ width: `${progressPct}%` }}
            />
          </div>

          <div className="grid grid-cols-2 gap-2 mt-3 text-[11px]">
            <div>
              <div className="text-[9.5px] uppercase tracking-wider font-bold text-arena-600 dark:text-neon-200/70">{t('agent.lockout.gauge.field.wallet')}</div>
              <div className={`text-[14px] font-bold tabular-nums ${wallet < 0 ? 'text-rose-700 dark:text-rose-300' : 'text-arena-900 dark:text-white'}`}>
                {inrSigned(wallet)}
              </div>
            </div>
            <div>
              <div className="text-[9.5px] uppercase tracking-wider font-bold text-arena-600 dark:text-neon-200/70">{t('agent.lockout.gauge.field.deposit')}</div>
              <div className="text-[14px] font-bold tabular-nums text-arena-900 dark:text-white">
                {inr(deposit)}
              </div>
            </div>
          </div>

          {/* Three-tier explainer table — same model as the form below,
              but compact so the agent sees their options up front. */}
          <div className="mt-3 rounded-xl border border-arena-200/60 dark:border-arena-600/50 overflow-hidden">
            <TierRow
              label={t('agent.lockout.tier.justUnlock.label')}
              tag={t('agent.lockout.tier.justUnlock.tag')}
              tagTone="rose"
              amount={bareMinimum}
              note={t('agent.lockout.tier.justUnlock.note')}
            />
            <TierRow
              label={t('agent.lockout.tier.safeMin.label')}
              tag={t('agent.lockout.tier.safeMin.tag')}
              tagTone="amber"
              amount={safeMinimum}
              note={t('agent.lockout.tier.safeMin.note', { deposit: inr(deposit) })}
              highlight
            />
            <TierRow
              label={t('agent.lockout.tier.fullRecovery.label')}
              tag={t('agent.lockout.tier.fullRecovery.tag')}
              tagTone="emerald"
              amount={recommended}
              note={t('agent.lockout.tier.fullRecovery.note')}
            />
          </div>
        </div>

        {/* ─── 4. TWO WAYS OUT (primary CTAs) ──────────────────── */}
        <div className="px-5 sm:px-7 py-5 border-t border-arena-200/60 dark:border-arena-600/50
                        bg-gradient-to-b from-arena-50/60 to-transparent
                        dark:from-arena-700/30 dark:to-transparent">
          <div className="text-[10.5px] uppercase tracking-wider font-bold text-arena-600 dark:text-neon-200/75 mb-2.5">
            {t('agent.lockout.waysOut.eyebrow')}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <WayOutCard
              step="1"
              accent="sky"
              icon="inbox"
              title={t('agent.lockout.waysOut.inbox.title')}
              body={t('agent.lockout.waysOut.inbox.body')}
              cta={t(inboxOpen ? 'agent.lockout.waysOut.inbox.ctaOpened' : 'agent.lockout.waysOut.inbox.ctaOpen')}
              ctaIcon={inboxOpen ? 'check' : 'arrow_downward'}
              onClick={() => {
                setInboxOpen(true);
                setTimeout(() => document.getElementById('bailout-inbox')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
              }}
            />
            <WayOutCard
              step="2"
              accent="amber"
              icon="north_east"
              title={t('agent.lockout.waysOut.upline.title')}
              body={t('agent.lockout.waysOut.upline.body')}
              cta={t(uplineOpen ? 'agent.lockout.waysOut.upline.ctaOpened' : 'agent.lockout.waysOut.upline.ctaOpen')}
              ctaIcon={uplineOpen ? 'check' : 'arrow_downward'}
              onClick={() => {
                setUplineOpen(true);
                setTimeout(() => document.getElementById('bailout-upline')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
              }}
            />
          </div>
        </div>

        {/* ─── 5. YOUR POWERS (CAN / CANNOT) ───────────────────── */}
        <div className="px-5 sm:px-7 py-5 border-t border-arena-200/60 dark:border-arena-600/50">
          <div className="text-[10.5px] uppercase tracking-wider font-bold text-arena-600 dark:text-neon-200/75 mb-2.5">
            {t('agent.lockout.capabilities.eyebrow')}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <CapabilityList
              tone="emerald"
              header={t('agent.lockout.capabilities.canHeader')}
              items={[
                t('agent.lockout.can.approvePlayerRecharge'),
                t('agent.lockout.can.markPlayerWithdrawalPaid'),
                t('agent.lockout.can.approveSubAgentRecharge'),
                t('agent.lockout.can.markSubAgentWithdrawalPaid'),
                t('agent.lockout.can.submitToUpline'),
                t('agent.lockout.can.receiveSseAndSignOut'),
              ]}
            />
            <CapabilityList
              tone="rose"
              header={t('agent.lockout.capabilities.cantHeader')}
              items={[
                t('agent.warning.ignore.item.dashboard'),
                t('agent.warning.ignore.item.pages'),
                t('agent.warning.ignore.item.manualTopup'),
                t('agent.warning.ignore.item.newWithdrawal'),
                t('agent.warning.ignore.item.subAgents'),
                t('agent.warning.ignore.item.profile'),
              ]}
            />
          </div>
        </div>

        {/* ─── 6. INBOX (Sections A + B) ──────────────────────── */}
        <div id="bailout-inbox" className="px-5 sm:px-7 py-5 border-t border-arena-200/60 dark:border-arena-600/50 scroll-mt-3">
          <SectionHeader
            stepNum="1"
            icon="inbox"
            accent="sky"
            title={t('agent.lockout.section.inbox.title')}
            subtitle={t('agent.lockout.section.inbox.subtitle')}
          />
          <div className="mt-3 space-y-4">
            <SubSection title={t('agent.lockout.section.inbox.fromPlayers')}>
              <PendingPlayerQueue refetchStatus={refetchStatus} />
            </SubSection>
            <SubSection title={t('agent.lockout.section.inbox.fromSubAgents')}>
              <PendingSubAgentQueue refetchStatus={refetchStatus} />
            </SubSection>
          </div>
        </div>

        {/* ─── 7. UPLINE PAYMENT (Section C) ──────────────────── */}
        <div id="bailout-upline" className="px-5 sm:px-7 py-5 border-t border-arena-200/60 dark:border-arena-600/50 scroll-mt-3">
          <SectionHeader
            stepNum="2"
            icon="north_east"
            accent="amber"
            title={t('agent.lockout.section.upline.title')}
            subtitle={t('agent.lockout.section.upline.subtitle')}
          />
          <div className="mt-3">
            <EmergencyRechargeForm severity="locked" />
          </div>
        </div>

        {/* ─── 8. REFRESH STATUS ───────────────────────────────── */}
        <div className="px-5 sm:px-7 py-5 border-t border-arena-200/60 dark:border-arena-600/50
                        bg-rose-50/60 dark:bg-rose-500/[0.06]">
          <button
            type="button"
            onClick={async () => {
              await refetchStatus();
              push('info', t('agent.lockout.refresh.toast'));
            }}
            disabled={refreshing}
            className="w-full inline-flex items-center justify-center gap-2
                       rounded-full px-4 py-3 text-[14px] font-bold transition active:scale-95
                       disabled:opacity-60
                       bg-rose-600 hover:bg-rose-500 active:bg-rose-700
                       text-white shadow-[0_4px_18px_rgba(244,63,94,0.5)]"
          >
            {refreshing
              ? <Icon name="progress_activity" size={16} className="animate-spin" />
              : <Icon name="refresh" size={16} />}
            {refreshing ? t('agent.lockout.refresh.checking') : t('agent.lockout.refresh.cta')}
          </button>
          <p className="text-center text-[11px] text-arena-600 dark:text-neon-200/65 mt-2 leading-snug">
            {t('agent.lockout.refresh.footnote')}
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────

function WayOutCard({
  step, accent, icon, title, body, cta, ctaIcon, onClick,
}: {
  step: string;
  accent: 'sky' | 'amber';
  icon: string;
  title: string;
  body: string;
  cta: string;
  ctaIcon: string;
  onClick: () => void;
}) {
  const cls = accent === 'sky'
    ? {
        bg:    'bg-gradient-to-br from-sky-500 to-sky-700',
        ring:  'ring-sky-300/40',
        shadow:'shadow-[0_10px_30px_-12px_rgba(14,165,233,0.55)]',
      }
    : {
        bg:    'bg-gradient-to-br from-amber-500 to-amber-700',
        ring:  'ring-amber-300/40',
        shadow:'shadow-[0_10px_30px_-12px_rgba(245,158,11,0.55)]',
      };
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative overflow-hidden rounded-2xl p-4 text-left text-white
                  ${cls.bg} ring-1 ${cls.ring} ${cls.shadow}
                  transition hover:scale-[1.01] active:scale-[0.99]`}
    >
      <div aria-hidden className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-white/15 blur-2xl" />
      <div className="relative">
        <div className="flex items-center gap-2 mb-1.5">
          <span className="grid place-items-center w-7 h-7 rounded-lg bg-white/20 ring-1 ring-white/25 text-[11px] font-black">
            {step}
          </span>
          <Icon name={icon} size={20} filled />
        </div>
        <div className="text-[15px] font-extrabold leading-tight">{title}</div>
        <p className="text-[12px] opacity-90 leading-snug mt-1.5">{body}</p>
        <div className="inline-flex items-center gap-1 mt-3 px-3 py-1.5 rounded-full
                        bg-white/20 text-[11.5px] font-bold uppercase tracking-wider">
          <Icon name={ctaIcon} size={13} />
          {cta}
        </div>
      </div>
    </button>
  );
}

// Phase 9b v2 — compact three-tier explainer row used inside the
// progress gauge. The full interactive picker lives in
// EmergencyRechargeForm; this is the read-only summary so the agent
// understands the options BEFORE scrolling to the form.
function TierRow({
  label, tag, tagTone, amount, note, highlight,
}: {
  label: string;
  tag: string;
  tagTone: 'rose' | 'amber' | 'emerald';
  amount: number;
  note: string;
  highlight?: boolean;
}) {
  const tagCls = {
    rose:    'bg-rose-100 text-rose-800 dark:bg-rose-500/20 dark:text-rose-200',
    amber:   'bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-200',
    emerald: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-200',
  }[tagTone];
  const rowCls = highlight
    ? 'bg-amber-50/70 dark:bg-amber-500/10'
    : 'bg-white dark:bg-arena-700/30';
  return (
    <div className={`px-3 py-2.5 border-b last:border-b-0 border-arena-200/60 dark:border-arena-600/40 ${rowCls}`}>
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[12.5px] font-bold text-arena-900 dark:text-white">{label}</span>
          <span className={`text-[9.5px] uppercase tracking-wider font-extrabold px-1.5 py-0.5 rounded ${tagCls}`}>
            {tag}
          </span>
        </div>
        <span className="font-mono font-black tabular-nums text-[15px] text-arena-900 dark:text-white">
          ₹{amount.toLocaleString('en-IN')}
        </span>
      </div>
      <p className="text-[10.5px] text-arena-600 dark:text-neon-200/65 leading-snug mt-0.5">{note}</p>
    </div>
  );
}

function CapabilityList({
  tone, header, items,
}: { tone: 'emerald' | 'rose'; header: string; items: string[] }) {
  const cls = tone === 'emerald'
    ? {
        wrap:   'bg-emerald-50/60 dark:bg-emerald-500/[0.08] border-emerald-200/60 dark:border-emerald-400/30',
        header: 'text-emerald-800 dark:text-emerald-200',
        pip:    'bg-emerald-500 text-white',
        icon:   'check' as const,
        text:   'text-arena-900 dark:text-white',
      }
    : {
        wrap:   'bg-rose-50/60 dark:bg-rose-500/[0.08] border-rose-200/60 dark:border-rose-400/30',
        header: 'text-rose-800 dark:text-rose-200',
        pip:    'bg-rose-500 text-white',
        icon:   'close' as const,
        text:   'text-arena-900 dark:text-white',
      };
  return (
    <div className={`rounded-2xl border p-3.5 ${cls.wrap}`}>
      <div className={`text-[11px] uppercase tracking-wider font-extrabold mb-2 ${cls.header}`}>
        {header}
      </div>
      <ul className="space-y-1.5">
        {items.map((t) => (
          <li key={t} className="flex items-start gap-2 text-[12.5px] leading-snug">
            <span className={`grid place-items-center w-4 h-4 rounded-full shrink-0 mt-0.5 ${cls.pip}`}>
              <Icon name={cls.icon} size={11} filled />
            </span>
            <span className={cls.text}>{t}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SectionHeader({
  stepNum, icon, accent, title, subtitle,
}: {
  stepNum: string;
  icon: string;
  accent: 'sky' | 'amber';
  title: string;
  subtitle: string;
}) {
  const t = useT();
  const cls = accent === 'sky'
    ? 'bg-sky-100 text-sky-800 ring-sky-200 dark:bg-sky-500/15 dark:text-sky-300 dark:ring-sky-400/30'
    : 'bg-amber-100 text-amber-800 ring-amber-200 dark:bg-amber-500/15 dark:text-amber-300 dark:ring-amber-400/30';
  return (
    <div className="flex items-start gap-3">
      {/* Relative wrapper so the step-number badge anchors correctly
          to the top-right of the icon tile. */}
      <span className="relative shrink-0">
        <span className={`grid place-items-center w-11 h-11 rounded-2xl ring-1 ${cls}`}>
          <Icon name={icon} size={22} filled />
        </span>
        <span className="absolute -top-1 -right-1 text-[10px] font-black px-1.5 py-0.5 rounded-full
                         bg-arena-900 dark:bg-white text-white dark:text-arena-900
                         shadow-[0_2px_6px_rgba(0,0,0,0.25)]">
          {stepNum}
        </span>
      </span>
      <div className="min-w-0 flex-1">
        <h2 className="text-[15px] font-bold text-arena-900 dark:text-white leading-tight">{t('agent.lockout.section.step', { n: stepNum, title })}</h2>
        <p className="text-[12px] text-arena-600 dark:text-neon-200/70 leading-snug mt-0.5">{subtitle}</p>
      </div>
    </div>
  );
}

function SubSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10.5px] uppercase tracking-wider font-bold text-arena-600 dark:text-neon-200/70 mb-1.5">
        {title}
      </div>
      {children}
    </div>
  );
}

// ─── Pending PLAYER recharges + withdrawals ─────────────────────

type PlayerRecharge = {
  id: number; user_id: number; username: string; amount: number;
  transaction_id: string; user_note: string | null;
  created_at: string; state: string;
};
type PlayerWithdrawal = {
  id: number; user_id: number; username: string; amount: number;
  upi_id: string; whatsapp_number: string | null;
  created_at: string; state: string;
};

function PendingPlayerQueue({ refetchStatus }: { refetchStatus: () => Promise<void> }) {
  const t = useT();
  const [rch, setRch] = useState<PlayerRecharge[] | null>(null);
  const [wd, setWd] = useState<PlayerWithdrawal[] | null>(null);
  const [busy, setBusy] = useState<Set<string>>(new Set());
  const [err, setErr] = useState<string | null>(null);
  const { push } = useToast();

  const load = useCallback(async () => {
    setErr(null);
    try {
      const [r, w] = await Promise.all([
        adminApi.get<{ rows: PlayerRecharge[] }>('/admin/payment-requests/recharge?state=pending'),
        adminApi.get<{ rows: PlayerWithdrawal[] }>('/admin/payment-requests/withdrawal?state=pending'),
      ]);
      setRch(r.rows || []);
      setWd(w.rows || []);
    } catch (e) { setErr((e as ApiError).message || 'Failed to load'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function actionRecharge(id: number, verb: 'approve' | 'reject') {
    const key = `r-${id}-${verb}`;
    setBusy((s) => new Set(s).add(key));
    try {
      await adminApi.post(`/admin/payment-requests/recharge/${id}/${verb}`, {});
      push('success', t(verb === 'approve' ? 'agent.lockout.queue.toast.rechargeApproved' : 'agent.lockout.queue.toast.rechargeRejected'));
      await Promise.all([load(), refetchStatus()]);
    } catch (e) {
      push('error', (e as ApiError).message || t('agent.lockout.queue.toast.actionFailed'));
    } finally {
      setBusy((s) => { const n = new Set(s); n.delete(key); return n; });
    }
  }
  async function markWithdrawalPaid(id: number) {
    const txn = prompt(t('agent.lockout.queue.prompt.markPlayerPaid'));
    if (!txn || txn.trim().length < 6) return;
    const key = `w-${id}-paid`;
    setBusy((s) => new Set(s).add(key));
    try {
      await adminApi.post(`/admin/payment-requests/withdrawal/${id}/mark-paid`, {
        paid_transaction_id: txn.trim(),
      });
      push('success', t('agent.lockout.queue.toast.withdrawalPaid'));
      await Promise.all([load(), refetchStatus()]);
    } catch (e) {
      push('error', (e as ApiError).message || t('agent.lockout.queue.toast.actionFailed'));
    } finally {
      setBusy((s) => { const n = new Set(s); n.delete(key); return n; });
    }
  }
  async function rejectWithdrawal(id: number) {
    if (!confirm(t('agent.lockout.queue.confirm.rejectPlayerWithdrawal'))) return;
    const key = `w-${id}-reject`;
    setBusy((s) => new Set(s).add(key));
    try {
      await adminApi.post(`/admin/payment-requests/withdrawal/${id}/reject`, {});
      push('success', t('agent.lockout.queue.toast.withdrawalRejected'));
      await Promise.all([load(), refetchStatus()]);
    } catch (e) {
      push('error', (e as ApiError).message || t('agent.lockout.queue.toast.actionFailed'));
    } finally {
      setBusy((s) => { const n = new Set(s); n.delete(key); return n; });
    }
  }

  if (err) return <ErrorRow msg={err} />;
  if (!rch || !wd) return <LoadingRow />;
  const total = rch.length + wd.length;
  if (total === 0) {
    return <EmptyRow icon="inbox" msg={t('agent.lockout.queue.empty.player')} />;
  }
  return (
    <div className="space-y-2">
      {rch.map((r) => (
        <QueueRow key={`r${r.id}`}
          accent="sky"
          icon="add_circle"
          title={t('agent.lockout.queue.player.recharge.title', { amount: inr(r.amount), username: r.username })}
          subtitle={r.user_note
            ? t('agent.lockout.queue.player.recharge.subtitleNote', { txn: r.transaction_id, when: formatWhen(r.created_at), note: r.user_note })
            : t('agent.lockout.queue.player.recharge.subtitle', { txn: r.transaction_id, when: formatWhen(r.created_at) })}
          primary={{
            label: t('agent.lockout.queue.action.approve'),
            disabled: busy.has(`r-${r.id}-approve`),
            onClick: () => actionRecharge(r.id, 'approve'),
          }}
          secondary={{
            label: t('agent.lockout.queue.action.reject'),
            disabled: busy.has(`r-${r.id}-reject`),
            onClick: () => actionRecharge(r.id, 'reject'),
          }}
        />
      ))}
      {wd.map((w) => (
        <QueueRow key={`w${w.id}`}
          accent="rose"
          icon="north_east"
          title={t('agent.lockout.queue.player.withdrawal.title', { amount: inr(w.amount), username: w.username })}
          subtitle={w.whatsapp_number
            ? t('agent.lockout.queue.player.withdrawal.subtitleWa', { upi: w.upi_id, whatsapp: w.whatsapp_number, when: formatWhen(w.created_at) })
            : t('agent.lockout.queue.player.withdrawal.subtitle', { upi: w.upi_id, when: formatWhen(w.created_at) })}
          primary={{
            label: t('agent.lockout.queue.action.markPaid'),
            disabled: busy.has(`w-${w.id}-paid`),
            onClick: () => markWithdrawalPaid(w.id),
          }}
          secondary={{
            label: t('agent.lockout.queue.action.reject'),
            disabled: busy.has(`w-${w.id}-reject`),
            onClick: () => rejectWithdrawal(w.id),
          }}
        />
      ))}
    </div>
  );
}

// ─── Pending SUB-AGENT recharges + withdrawals ──────────────────

type SubAgentRecharge = {
  id: number; agent_id: number; agent_username: string; amount: number;
  transaction_id: string; agent_note: string | null;
  created_at: string; state: string;
};
type SubAgentWithdrawal = {
  id: number; agent_id: number; agent_username: string; amount: number;
  upi_id: string; agent_note: string | null;
  created_at: string; state: string;
};

function PendingSubAgentQueue({ refetchStatus }: { refetchStatus: () => Promise<void> }) {
  const t = useT();
  const [rch, setRch] = useState<SubAgentRecharge[] | null>(null);
  const [wd, setWd] = useState<SubAgentWithdrawal[] | null>(null);
  const [busy, setBusy] = useState<Set<string>>(new Set());
  const [err, setErr] = useState<string | null>(null);
  const { push } = useToast();

  const load = useCallback(async () => {
    setErr(null);
    try {
      const [r, w] = await Promise.all([
        adminApi.get<{ rows: SubAgentRecharge[] }>('/admin/me-agent/payment-requests/agent-recharge?state=pending'),
        adminApi.get<{ rows: SubAgentWithdrawal[] }>('/admin/me-agent/payment-requests/agent-withdrawal?state=pending'),
      ]);
      setRch(r.rows || []);
      setWd(w.rows || []);
    } catch (e) { setErr((e as ApiError).message || 'Failed to load'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function actionRecharge(id: number, verb: 'approve' | 'reject') {
    const key = `ar-${id}-${verb}`;
    setBusy((s) => new Set(s).add(key));
    try {
      await adminApi.post(`/admin/me-agent/payment-requests/agent-recharge/${id}/${verb}`, {});
      push('success', t(verb === 'approve' ? 'agent.lockout.queue.toast.subAgentRechargeApproved' : 'agent.lockout.queue.toast.subAgentRechargeRejected'));
      await Promise.all([load(), refetchStatus()]);
    } catch (e) {
      push('error', (e as ApiError).message || t('agent.lockout.queue.toast.actionFailed'));
    } finally {
      setBusy((s) => { const n = new Set(s); n.delete(key); return n; });
    }
  }
  async function markWithdrawalPaid(id: number) {
    const txn = prompt(t('agent.lockout.queue.prompt.markSubAgentPaid'));
    if (!txn || txn.trim().length < 6) return;
    const key = `aw-${id}-paid`;
    setBusy((s) => new Set(s).add(key));
    try {
      await adminApi.post(`/admin/me-agent/payment-requests/agent-withdrawal/${id}/mark-paid`, {
        paid_transaction_id: txn.trim(),
      });
      push('success', t('agent.lockout.queue.toast.subAgentWithdrawalPaid'));
      await Promise.all([load(), refetchStatus()]);
    } catch (e) {
      push('error', (e as ApiError).message || t('agent.lockout.queue.toast.actionFailed'));
    } finally {
      setBusy((s) => { const n = new Set(s); n.delete(key); return n; });
    }
  }
  async function rejectWithdrawal(id: number) {
    if (!confirm(t('agent.lockout.queue.confirm.rejectSubAgentWithdrawal'))) return;
    const key = `aw-${id}-reject`;
    setBusy((s) => new Set(s).add(key));
    try {
      await adminApi.post(`/admin/me-agent/payment-requests/agent-withdrawal/${id}/reject`, {});
      push('success', t('agent.lockout.queue.toast.subAgentWithdrawalRejected'));
      await Promise.all([load(), refetchStatus()]);
    } catch (e) {
      push('error', (e as ApiError).message || t('agent.lockout.queue.toast.actionFailed'));
    } finally {
      setBusy((s) => { const n = new Set(s); n.delete(key); return n; });
    }
  }

  if (err) return <ErrorRow msg={err} />;
  if (!rch || !wd) return <LoadingRow />;
  const total = rch.length + wd.length;
  if (total === 0) {
    return <EmptyRow icon="support_agent" msg={t('agent.lockout.queue.empty.subAgent')} />;
  }
  return (
    <div className="space-y-2">
      {rch.map((r) => (
        <QueueRow key={`ar${r.id}`}
          accent="emerald"
          icon="add_circle"
          title={t('agent.lockout.queue.subAgent.recharge.title', { amount: inr(r.amount), username: r.agent_username })}
          subtitle={r.agent_note
            ? t('agent.lockout.queue.subAgent.recharge.subtitleNote', { txn: r.transaction_id, when: formatWhen(r.created_at), note: r.agent_note })
            : t('agent.lockout.queue.subAgent.recharge.subtitle', { txn: r.transaction_id, when: formatWhen(r.created_at) })}
          primary={{
            label: t('agent.lockout.queue.action.approve'),
            disabled: busy.has(`ar-${r.id}-approve`),
            onClick: () => actionRecharge(r.id, 'approve'),
          }}
          secondary={{
            label: t('agent.lockout.queue.action.reject'),
            disabled: busy.has(`ar-${r.id}-reject`),
            onClick: () => actionRecharge(r.id, 'reject'),
          }}
        />
      ))}
      {wd.map((w) => (
        <QueueRow key={`aw${w.id}`}
          accent="rose"
          icon="north_east"
          title={t('agent.lockout.queue.subAgent.withdrawal.title', { amount: inr(w.amount), username: w.agent_username })}
          subtitle={w.agent_note
            ? t('agent.lockout.queue.subAgent.withdrawal.subtitleNote', { upi: w.upi_id, when: formatWhen(w.created_at), note: w.agent_note })
            : t('agent.lockout.queue.subAgent.withdrawal.subtitle', { upi: w.upi_id, when: formatWhen(w.created_at) })}
          primary={{
            label: t('agent.lockout.queue.action.markPaid'),
            disabled: busy.has(`aw-${w.id}-paid`),
            onClick: () => markWithdrawalPaid(w.id),
          }}
          secondary={{
            label: t('agent.lockout.queue.action.reject'),
            disabled: busy.has(`aw-${w.id}-reject`),
            onClick: () => rejectWithdrawal(w.id),
          }}
        />
      ))}
    </div>
  );
}

// ─── Shared queue row + state primitives ────────────────────────

function QueueRow({
  accent, icon, title, subtitle, primary, secondary,
}: {
  accent: 'sky' | 'rose' | 'emerald';
  icon: string;
  title: string;
  subtitle: string;
  primary: { label: string; onClick: () => void; disabled?: boolean };
  secondary?: { label: string; onClick: () => void; disabled?: boolean };
}) {
  const accentCls = {
    sky:     'bg-sky-50 text-sky-700 ring-sky-200 dark:bg-sky-400/15 dark:text-sky-300 dark:ring-sky-400/30',
    rose:    'bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-400/15 dark:text-rose-300 dark:ring-rose-400/30',
    emerald: 'bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-400/15 dark:text-emerald-300 dark:ring-emerald-400/30',
  }[accent];
  const primaryCls = {
    sky:     'bg-sky-500 hover:bg-sky-400 active:bg-sky-600 shadow-[0_2px_8px_rgba(14,165,233,0.4)]',
    rose:    'bg-rose-500 hover:bg-rose-400 active:bg-rose-600 shadow-[0_2px_8px_rgba(244,63,94,0.4)]',
    emerald: 'bg-emerald-500 hover:bg-emerald-400 active:bg-emerald-600 shadow-[0_2px_8px_rgba(16,185,129,0.4)]',
  }[accent];
  return (
    <div className="rounded-xl p-3
                    bg-white dark:bg-arena-700/40
                    border border-arena-200/60 dark:border-arena-600/50">
      <div className="flex items-start gap-2.5">
        <span className={`grid place-items-center w-8 h-8 rounded-lg ring-1 shrink-0 ${accentCls}`}>
          <Icon name={icon} size={16} filled />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-bold text-arena-900 dark:text-white leading-tight break-words">{title}</div>
          <div className="text-[11px] text-arena-600 dark:text-neon-200/70 leading-snug mt-0.5 break-words">{subtitle}</div>
        </div>
      </div>
      <div className="flex items-center justify-end gap-2 mt-2.5">
        {secondary && (
          <button
            type="button"
            onClick={secondary.onClick}
            disabled={secondary.disabled}
            className="px-3 py-1.5 rounded-full text-[12px] font-bold
                       bg-white dark:bg-arena-700/55 text-arena-700 dark:text-neon-200/85
                       border border-arena-200/60 dark:border-arena-600/50
                       hover:bg-arena-50/40 dark:hover:bg-arena-600/60
                       transition active:scale-95 disabled:opacity-50"
          >
            {secondary.label}
          </button>
        )}
        <button
          type="button"
          onClick={primary.onClick}
          disabled={primary.disabled}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-bold
                      text-white transition active:scale-95 disabled:opacity-50 ${primaryCls}`}
        >
          {primary.disabled ? <Icon name="progress_activity" size={13} className="animate-spin" /> : <Icon name="check" size={13} />}
          {primary.label}
        </button>
      </div>
    </div>
  );
}

function LoadingRow() {
  const t = useT();
  return (
    <div className="rounded-xl px-3 py-4 text-center text-[12px] text-arena-600 dark:text-neon-200/70
                    bg-arena-50/40 dark:bg-arena-700/30
                    border border-arena-200/60 dark:border-arena-600/50">
      <Icon name="progress_activity" size={16} className="inline-block animate-spin mr-1.5 align-middle" />
      {t('agent.lockout.queue.loading')}
    </div>
  );
}

function EmptyRow({ icon, msg }: { icon: string; msg: string }) {
  return (
    <div className="rounded-xl px-3 py-5 text-center
                    bg-arena-50/40 dark:bg-arena-700/30
                    border border-dashed border-arena-200/60 dark:border-arena-600/50">
      <Icon name={icon} size={22} className="text-arena-500 dark:text-neon-200/45 mx-auto mb-1.5" />
      <div className="text-[12px] text-arena-600 dark:text-neon-200/70">{msg}</div>
    </div>
  );
}

function ErrorRow({ msg }: { msg: string }) {
  return (
    <div className="rounded-xl px-3 py-2.5 text-[12px]
                    bg-rose-50 dark:bg-rose-500/15
                    border border-rose-200 dark:border-rose-400/30
                    text-rose-700 dark:text-rose-200">
      {msg}
    </div>
  );
}

function formatWhen(s: string): string {
  try {
    return new Date(s).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' });
  } catch { return s; }
}
